'use client';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  type ClaireFailureGroupStatus,
  type ClaireFailureGroupView,
  claireFailureGroupStatusLabels,
  claireFailureGroupStatusValues,
} from '@borradh-workspace/api-client/types';
import {
  ChevronDown,
  ExternalLink,
  Layers,
  Pencil,
  Wand2,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  useUnassignClaireFailure,
  useUpdateClaireFailureGroup,
} from '../../api/claire-failure-groups';
import {
  failureTypeLabel,
  harnessUrl,
  isSeriousType,
  shortDateTime,
} from './failure-format';
import { GroupDialog } from './group-dialog';
import { StartFixDialog } from './start-fix-dialog';

const plural = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

function GroupRow({ group }: { group: ClaireFailureGroupView }) {
  const [editing, setEditing] = useState(false);
  const [startingFix, setStartingFix] = useState(false);
  const { updateGroup, isUpdating } = useUpdateClaireFailureGroup({
    onSuccess: () => setEditing(false),
  });
  const { unassignFailure, isUnassigning } = useUnassignClaireFailure();
  // Stable while the group is unchanged: the dialog resets the form when
  // this changes, so a fresh object every render would wipe typed edits.
  const initial = useMemo(
    () => ({
      title: group.title,
      description: group.description,
      linearUrl: group.linearUrl ?? '',
      fixPrUrl: group.fixPrUrl ?? '',
    }),
    [group.title, group.description, group.linearUrl, group.fixPrUrl]
  );

  return (
    <Collapsible className="rounded-md border" data-testid="failure-group">
      <div className="flex flex-wrap items-start gap-4 p-4">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold">{group.title}</h3>
            {group.failureType ? (
              <Badge
                variant={
                  isSeriousType(group.failureType) ? 'destructive' : 'secondary'
                }
              >
                {failureTypeLabel(group.failureType)}
              </Badge>
            ) : null}
            {group.reopenedAt && group.status !== 'closed' ? (
              <Badge variant="outline">
                Reopened {shortDateTime(group.reopenedAt)}
              </Badge>
            ) : null}
          </div>
          {group.description ? (
            <p className="text-muted-foreground mt-1 text-sm">
              {group.description}
            </p>
          ) : null}
          <p className="mt-2 text-sm">
            {plural(group.failures, 'reply', 'replies')} ·{' '}
            {plural(group.conversations, 'conversation', 'conversations')} ·{' '}
            {plural(group.clinics, 'clinic', 'clinics')} ·{' '}
            <strong>{group.lastWeek}</strong> in the last 7 days
            {group.lastSeenAt
              ? ` · last ${shortDateTime(group.lastSeenAt)}`
              : ''}
          </p>
          <div className="mt-2 flex flex-wrap gap-3 text-sm">
            {group.linearUrl ? (
              <a
                className="inline-flex items-center gap-1 underline"
                href={group.linearUrl}
                target="_blank"
                rel="noreferrer"
              >
                Linear issue <ExternalLink className="size-3" />
              </a>
            ) : (
              <span className="text-muted-foreground">No Linear issue</span>
            )}
            {group.fixPrUrl ? (
              <a
                className="inline-flex items-center gap-1 underline"
                href={group.fixPrUrl}
                target="_blank"
                rel="noreferrer"
              >
                Fix PR <ExternalLink className="size-3" />
              </a>
            ) : (
              <span className="text-muted-foreground">No fix PR</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select
            value={group.status}
            disabled={isUpdating}
            onValueChange={(status) =>
              updateGroup({
                id: group.id,
                status: status as ClaireFailureGroupStatus,
              })
            }
          >
            <SelectTrigger
              className="w-40"
              aria-label={`Status of ${group.title}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {claireFailureGroupStatusValues.map((s) => (
                <SelectItem key={s} value={s}>
                  {claireFailureGroupStatusLabels[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Start a fix for ${group.title}`}
            title="Start a fix"
            disabled={group.status === 'closed' || group.members.length === 0}
            onClick={() => setStartingFix(true)}
          >
            <Wand2 />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            aria-label={`Edit ${group.title}`}
            onClick={() => setEditing(true)}
          >
            <Pencil />
          </Button>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm">
              Replies
              <ChevronDown />
            </Button>
          </CollapsibleTrigger>
        </div>
      </div>
      <CollapsibleContent>
        <ul className="divide-y border-t text-sm">
          {group.members.map((m) => (
            <li
              key={m.observationId}
              className="flex flex-wrap items-center gap-3 px-4 py-2"
            >
              <span className="whitespace-nowrap">
                {shortDateTime(m.turnAt)}
              </span>
              <span>{m.clinicName ?? 'Unknown clinic'}</span>
              <span className="text-muted-foreground">
                {failureTypeLabel(m.failureType)}
              </span>
              <span className="ml-auto flex items-center gap-3">
                {m.conversationId ? (
                  <a className="underline" href={harnessUrl(m.conversationId)}>
                    Chat
                  </a>
                ) : null}
                {m.langfuseUrl ? (
                  <a
                    className="underline"
                    href={m.langfuseUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Trace
                  </a>
                ) : null}
                <Button
                  variant="ghost"
                  size="icon"
                  disabled={isUnassigning}
                  aria-label={`Take the reply sent ${shortDateTime(m.turnAt)} out of this group`}
                  onClick={() => unassignFailure(m.observationId)}
                >
                  <X />
                </Button>
              </span>
            </li>
          ))}
        </ul>
      </CollapsibleContent>
      <StartFixDialog
        group={group}
        open={startingFix}
        onOpenChange={setStartingFix}
      />
      <GroupDialog
        open={editing}
        onOpenChange={setEditing}
        mode="edit"
        isSaving={isUpdating}
        initial={initial}
        onSubmit={(values) =>
          updateGroup({
            id: group.id,
            title: values.title,
            description: values.description,
            linearUrl: values.linearUrl || null,
            fixPrUrl: values.fixPrUrl || null,
          })
        }
      />
    </Collapsible>
  );
}

/** Root causes, open and busiest first; each says how often it happens. */
export function FailureGroupsTable({
  groups,
}: {
  groups: ClaireFailureGroupView[];
}) {
  const open = groups.filter((g) => g.status !== 'closed').length;
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          Failure groups ({open} open
          {groups.length > open ? `, ${groups.length - open} closed` : ''})
        </CardTitle>
        <CardDescription>
          Each group is one cause behind several bad replies, so it gets fixed
          once. Move a group along as its fix progresses: needs a fix → fix in
          progress → fix shipped → closed. A new reply added to a group whose
          fix shipped, or that was closed, reopens it.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {groups.length === 0 ? (
          <Empty className="border">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <Layers />
              </EmptyMedia>
              <EmptyTitle>No groups yet</EmptyTitle>
              <EmptyDescription>
                Select confirmed replies above and choose New group.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          groups.map((g) => <GroupRow key={g.id} group={g} />)
        )}
      </CardContent>
    </Card>
  );
}
